import { useState } from "react";
import { ESTADOS_EDIFICIO_LISTA } from "../../constants/estadosEdificio";
import { PinPicker } from "../PinPicker";

// Form puro y controlado de edificios (Bloque C, puerta única). Reusable crear/editar.
// En Fase 3a se usa solo en modo "editar" (campos NO-comisión). El bloque comisión va
// VISIBLE pero READ-ONLY (su edición es Fase 4). onSubmit entrega el OBJETO ENTERO del
// edificio (regla 10.54): parte del `edificio` inicial y pisa con lo editado, re-pasando
// el bloque comisión tal cual vino para no nulificarlo.
//
// Props: { modo: "crear"|"editar", edificio (objeto inicial|null), pais, proveedores,
//          onSubmit(objEdificioCompleto), onCancel }

const UBICACIONES_CHILE = ["Centro", "Huechuraba", "Las Condes", "Providencia", "Vitacura"];
const UBICACIONES_PERU = ["Magdalena", "Miraflores", "San Isidro", "Surco", "Surquillo"];

const fmtFecha = (f) => (f ? f : "—");
const fmtPct = (p) => (p === null || p === undefined || p === "" ? "—" : `${p}%`);

export const EdificioForm = ({ modo = "editar", edificio = null, pais, proveedores = [], onSubmit, onCancel }) => {
  const init = edificio || {};

  const [form, setForm] = useState({
    edificio: init.edificio || "",
    estado: init.estado || "",
    ubicacion: init.ubicacion || "",
    direccion: init.direccion || "",
    personas: init.personas ?? "",
    pantallas: init.pantallas ?? "",
    totems: init.totems ?? "",
    espera: init.espera ?? "",
    cowork: init.cowork ?? "",
    estacionamientos: init.estacionamientos ?? "",
    estacionamientos_ocupacion: init.estacionamientos_ocupacion ?? "",
    alianza: init.alianza || "",
    is_programmatic: init.is_programmatic === true,
    proveedor_id: init.proveedor_id ?? "",
    empresaslistado: init.empresaslistado || "",
    empresasdestacadas: init.empresasdestacadas || "",
    vetos: init.vetos || "",
    lat: init.lat ?? null,
    lng: init.lng ?? null,
  });

  const [errors, setErrors] = useState({});

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Estados: set canónico (7) desde fuente única. Si el edificio trae un estado
  // legacy fuera del set (ej. PRÓXIMO Uruguay), se preserva como opción extra para
  // no forzar un cambio silencioso al guardar.
  const estadosOpts =
    form.estado && !ESTADOS_EDIFICIO_LISTA.includes(form.estado)
      ? [form.estado, ...ESTADOS_EDIFICIO_LISTA]
      : ESTADOS_EDIFICIO_LISTA;

  const ubicacionesBase = pais === "Perú" ? UBICACIONES_PERU : UBICACIONES_CHILE;
  const ubicacionesOpts =
    form.ubicacion && !ubicacionesBase.includes(form.ubicacion)
      ? [form.ubicacion, ...ubicacionesBase]
      : ubicacionesBase;

  const validate = (f) => {
    const e = {};
    if (!f.edificio || !f.edificio.trim()) e.edificio = "Nombre obligatorio";
    if (!f.estado) e.estado = "Estado obligatorio";
    if (!f.ubicacion || !String(f.ubicacion).trim()) e.ubicacion = "Ubicación obligatoria";
    if (!f.direccion || !String(f.direccion).trim()) e.direccion = "Dirección obligatoria";
    if (f.personas === "" || f.personas === null || Number(f.personas) <= 0) e.personas = "Audiencia obligatoria (> 0)";
    const circuitos = [f.pantallas, f.totems, f.espera, f.cowork].map((v) => Number(v) || 0);
    if (!circuitos.some((n) => n > 0)) e.pantallas = "Al menos un circuito con pantallas (> 0)";
    if (f.lat === null || f.lat === "" || f.lng === null || f.lng === "") {
      e.lat = "Ubicación en el mapa obligatoria — arrastrá el pin";
    }
    return e;
  };

  const handleSubmit = () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Objeto ENTERO: parte del edificio inicial (comisión + lo no editado se re-pasa
    // intacto) y pisa con los campos del form. Programática se persiste en el booleano
    // is_programmatic (dato autoritativo del cotizador). La columna texto `programatica`
    // es legacy/obsoleta: el form NO la setea (no se incluye aquí); en editar se re-pasa
    // intacta vía ...edificio, sin tocar el valor existente.
    const obj = {
      ...edificio,
      edificio: form.edificio.trim(),
      estado: form.estado,
      ubicacion: form.ubicacion,
      direccion: form.direccion,
      personas: form.personas,
      pantallas: form.pantallas,
      totems: form.totems,
      espera: form.espera,
      cowork: form.cowork,
      estacionamientos: form.estacionamientos,
      estacionamientos_ocupacion: form.estacionamientos_ocupacion,
      alianza: form.alianza,
      is_programmatic: form.is_programmatic,
      lat: form.lat,
      lng: form.lng,
    };

    // Empresas / vetos / proveedor: campos que SOLO se editan en modo editar (sus inputs
    // están ocultos en crear). En editar se re-pasan intactos (regla 10.54). En crear NO
    // entran al objeto → el edificio nace sin setearlos (columnas nullable).
    if (modo === "editar") {
      obj.proveedor_id = form.proveedor_id === "" ? null : form.proveedor_id;
      obj.empresaslistado = form.empresaslistado;
      obj.empresasdestacadas = form.empresasdestacadas;
      obj.vetos = form.vetos;
    }

    onSubmit(obj);
  };

  const inputClass = (key) => `form-control${errors[key] ? " is-invalid" : ""}`;

  return (
    <div className={modo === "crear" ? "row g-2" : "row g-3"}>
      {/* ── Identidad / ubicación ── */}
      <div className="col-3">
        <label className="form-label">Nombre *</label>
        <input
          className={inputClass("edificio")}
          type="text"
          value={form.edificio}
          onChange={(e) => setField("edificio", e.target.value)}
        />
        {errors.edificio && <div className="invalid-feedback">{errors.edificio}</div>}
      </div>
      <div className="col-3">
        <label className="form-label">Estado *</label>
        <select
          className={`form-select${errors.estado ? " is-invalid" : ""}`}
          value={form.estado}
          onChange={(e) => setField("estado", e.target.value)}
        >
          <option value="">-</option>
          {estadosOpts.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {errors.estado && <div className="invalid-feedback">{errors.estado}</div>}
      </div>
      <div className="col-3">
        <label className="form-label">Ubicación *</label>
        <select
          className={`form-select${errors.ubicacion ? " is-invalid" : ""}`}
          value={form.ubicacion}
          onChange={(e) => setField("ubicacion", e.target.value)}
        >
          <option value="">-</option>
          {ubicacionesOpts.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
        {errors.ubicacion && <div className="invalid-feedback">{errors.ubicacion}</div>}
      </div>
      <div className="col-3">
        <label className="form-label">Dirección *</label>
        <input
          className={inputClass("direccion")}
          type="text"
          value={form.direccion}
          onChange={(e) => setField("direccion", e.target.value)}
        />
        {errors.direccion && <div className="invalid-feedback">{errors.direccion}</div>}
      </div>

      {/* ── Audiencia / estacionamiento / alianza / programática ── */}
      <div className="col-3">
        <label className="form-label">Audiencia *</label>
        <input
          className={inputClass("personas")}
          type="number"
          value={form.personas}
          onChange={(e) => setField("personas", e.target.value)}
        />
        {errors.personas && <div className="invalid-feedback">{errors.personas}</div>}
      </div>
      <div className="col-2">
        <label className="form-label">Estacionamientos</label>
        <input
          className="form-control"
          type="number"
          min="0"
          value={form.estacionamientos}
          onChange={(e) => setField("estacionamientos", e.target.value)}
        />
      </div>
      <div className="col-2">
        <label className="form-label">Ocupación estac.</label>
        <input
          className="form-control"
          type="number"
          min="0"
          value={form.estacionamientos_ocupacion}
          onChange={(e) => setField("estacionamientos_ocupacion", e.target.value)}
        />
      </div>
      <div className="col-3">
        <label className="form-label">Alianza</label>
        <input
          className="form-control"
          type="text"
          value={form.alianza}
          onChange={(e) => setField("alianza", e.target.value)}
        />
      </div>
      <div className="col-2">
        <label className="form-label d-block">Programática</label>
        <div className="form-check mt-1">
          <input
            className="form-check-input"
            type="checkbox"
            id="is_programmatic"
            checked={form.is_programmatic}
            onChange={(e) => setField("is_programmatic", e.target.checked)}
          />
          <label className="form-check-label" htmlFor="is_programmatic">
            Sí
          </label>
        </div>
      </div>

      {/* ── Pantallas (≥1 circuito > 0) ── */}
      <div className="col-3">
        <label className="form-label">Pantallas ASC</label>
        <input
          className={inputClass("pantallas")}
          type="number"
          min="0"
          value={form.pantallas}
          onChange={(e) => setField("pantallas", e.target.value)}
        />
        {errors.pantallas && <div className="invalid-feedback">{errors.pantallas}</div>}
      </div>
      <div className="col-3">
        <label className="form-label">Tótems CIR</label>
        <input
          className="form-control"
          type="number"
          min="0"
          value={form.totems}
          onChange={(e) => setField("totems", e.target.value)}
        />
      </div>
      <div className="col-3">
        <label className="form-label">Espera</label>
        <input
          className="form-control"
          type="number"
          min="0"
          value={form.espera}
          onChange={(e) => setField("espera", e.target.value)}
        />
      </div>
      <div className="col-3">
        <label className="form-label">Cowork</label>
        <input
          className="form-control"
          type="number"
          min="0"
          value={form.cowork}
          onChange={(e) => setField("cowork", e.target.value)}
        />
      </div>

      {/* ── Proveedor (solo editar; programática es ahora checkbox en la fila de arriba) ── */}
      {modo === "editar" && (
        <div className="col-3">
          <label className="form-label">Proveedor</label>
          <select
            className="form-select"
            value={form.proveedor_id ?? ""}
            onChange={(e) => setField("proveedor_id", e.target.value ? parseInt(e.target.value, 10) : "")}
          >
            <option value="">Proveedor...</option>
            {proveedores.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Empresas / vetos (solo editar; en crear se cargan luego desde la fila) ── */}
      {modo === "editar" && (
        <>
          <div className="col-6">
            <label className="form-label">Empresas listado</label>
            <input
              className="form-control"
              type="text"
              placeholder="Nombres separados por coma"
              value={form.empresaslistado}
              onChange={(e) => setField("empresaslistado", e.target.value)}
            />
          </div>
          <div className="col-6">
            <label className="form-label">Empresas destacadas</label>
            <input
              className="form-control"
              type="text"
              placeholder="Nombres separados por coma"
              value={form.empresasdestacadas}
              onChange={(e) => setField("empresasdestacadas", e.target.value)}
            />
          </div>
          <div className="col-12">
            <label className="form-label">Vetos (IDs de categoría, separados por coma)</label>
            <input
              className="form-control"
              type="text"
              value={form.vetos}
              onChange={(e) => setField("vetos", e.target.value)}
            />
            <small className="text-muted">También editable con checkboxes desde el botón “Vetos” de la fila.</small>
          </div>
        </>
      )}

      {/* ── Ubicación en el mapa (lat/lng vía PinPicker) ── */}
      <div className="col-12">
        <label className="form-label">Ubicación en el mapa *</label>
        <PinPicker
          initialLat={form.lat}
          initialLng={form.lng}
          pais={pais}
          onChange={(lat, lng) => {
            setForm((f) => ({ ...f, lat, lng }));
          }}
        />
        {errors.lat && <div style={{ color: "#dc3545", fontSize: "0.875em", marginTop: 4 }}>{errors.lat}</div>}
      </div>

      {/* ── Bloque comisión (READ-ONLY en 3a; oculto en crear, nace sin comisión) ── */}
      {modo === "editar" && (
        <div className="col-12">
          <div style={{ border: "1px solid #dee2e6", borderRadius: 8, padding: 12, background: "#f8f9fa" }}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>
              Comisión{" "}
              <span style={{ fontWeight: "normal", color: "#888" }}>· Edición de comisión: Fase siguiente</span>
            </div>
            <div className="row">
              <div className="col-4">
                Comisión 1: {fmtPct(init.comision_1_porcentaje)}
                <div className="text-muted" style={{ fontSize: "0.8em" }}>
                  {fmtFecha(init.comision_1_fechainicio)} → {fmtFecha(init.comision_1_fechafin)}
                </div>
              </div>
              <div className="col-4">
                Comisión 2: {fmtPct(init.comision_2_porcentaje)}
                <div className="text-muted" style={{ fontSize: "0.8em" }}>
                  {fmtFecha(init.comision_2_fechainicio)} → {fmtFecha(init.comision_2_fechafin)}
                </div>
              </div>
              <div className="col-4">
                Comisión 3: {fmtPct(init.comision_3_porcentaje)}
                <div className="text-muted" style={{ fontSize: "0.8em" }}>
                  {fmtFecha(init.comision_3_fechainicio)} → {fmtFecha(init.comision_3_fechafin)}
                </div>
              </div>
            </div>
            <div className="mt-2">
              Comisión fija:{" "}
              {init.comision_fija_monto ? `${init.comision_fija_monto} ${init.comision_fija_moneda || ""}` : "—"}
              <span className="text-muted" style={{ fontSize: "0.8em" }}>
                {" "}
                · {fmtFecha(init.comision_fija_inicio)} → {fmtFecha(init.comision_fija_fin)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Acciones ── */}
      <div className="col-12 d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSubmit}>
          Guardar
        </button>
      </div>
    </div>
  );
};
